// File: frontend/src/components/AddEditBusinessForm.js
import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, MapPin, Building2, User, Phone, Mail, Globe, Link, Search, Folder, Briefcase } from 'lucide-react';
import { businessAPI, peopleAPI, casesAPI } from '../utils/api';

const B2B_RELATIONSHIP_TYPES = [
  { value: 'partner',    label: 'Partner' },
  { value: 'subsidiary', label: 'Subsidiary / Parent' },
  { value: 'competitor', label: 'Competitor' },
  { value: 'supplier',   label: 'Supplier' },
  { value: 'client',     label: 'Client' },
  { value: 'affiliate',  label: 'Affiliate' },
  { value: 'investor',   label: 'Investor' },
  { value: 'other',      label: 'Other' },
];

const AddEditBusinessForm = ({ business, onSave, onCancel }) => {
  const [people, setPeople] = useState([]);
  const [allCases, setAllCases] = useState([]);
  const [caseSearch, setCaseSearch] = useState('');
  const [showCaseSearch, setShowCaseSearch] = useState(false);
  const [selectedCaseIds, setSelectedCaseIds] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    type: '',
    industry: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postal_code: '',
    latitude: null,
    longitude: null,
    phone: '',
    email: '',
    website: '',
    owner_person_id: null,
    registration_number: '',
    registration_date: '',
    status: 'active',
    notes: ''
  });
  
  const [linkedPeople, setLinkedPeople] = useState([]);
  const [selectedPeopleIds, setSelectedPeopleIds] = useState(new Set());
  const [initialPeopleIds, setInitialPeopleIds] = useState(new Set());
  const [peopleSearch, setPeopleSearch] = useState('');

  const [selectedEmployedIds, setSelectedEmployedIds] = useState(new Set());
  const [initialEmployedIds, setInitialEmployedIds] = useState(new Set());
  const [employedSearch, setEmployedSearch] = useState('');

  // B2B connections
  const [bizConnections, setBizConnections] = useState([]);
  const [newBizConn, setNewBizConn] = useState({ targetId: '', type: 'partner', note: '' });
  const [showBizConnForm, setShowBizConnForm] = useState(false);
  const [allBusinesses, setAllBusinesses] = useState([]);

  useEffect(() => {
    fetchPeople();
    fetchAllBusinesses();
    casesAPI.getAll().then(data => setAllCases(data)).catch(() => {});
    if (business && business.cases_data) {
      setSelectedCaseIds((business.cases_data || []).map(c => c.id));
    }
    if (business && business.linked_people) {
      setLinkedPeople(business.linked_people);
      const ids = new Set(business.linked_people.map(p => p.id));
      setSelectedPeopleIds(ids);
      setInitialPeopleIds(ids);
    }
    if (business && business.employed_people) {
      const ids = new Set(business.employed_people.map(p => p.id));
      setSelectedEmployedIds(ids);
      setInitialEmployedIds(ids);
    }
    if (business && business.business_connections) {
      setBizConnections(business.business_connections);
    }
    if (business) {
      setFormData({
        name: business.name || '',
        type: business.type || '',
        industry: business.industry || '',
        address: business.address || '',
        city: business.city || '',
        state: business.state || '',
        country: business.country || '',
        postal_code: business.postal_code || '',
        latitude: business.latitude,
        longitude: business.longitude,
        phone: business.phone || '',
        email: business.email || '',
        website: business.website || '',
        owner_person_id: business.owner_person_id,
        registration_number: business.registration_number || '',
        registration_date: business.registration_date ? business.registration_date.split('T')[0] : '',
        status: business.status || 'active',
        notes: business.notes || ''
      });
    }
  }, [business]);

  const fetchPeople = async () => {
    try {
      const result = await peopleAPI.getAll();
      setPeople(result.data || result);
    } catch (error) {
      console.error('Error fetching people:', error);
    }
  };

  const fetchAllBusinesses = async () => {
    try {
      const data = await businessAPI.getAll();
      setAllBusinesses(data);
    } catch (error) {
      console.error('Error fetching businesses:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let savedBusiness;
      const dataWithCases = { ...formData, caseIds: selectedCaseIds };
      if (business) {
        savedBusiness = await businessAPI.update(business.id, dataWithCases);
      } else {
        savedBusiness = await businessAPI.create(dataWithCases);
      }

      const businessId = savedBusiness.id;
      const toAdd        = [...selectedPeopleIds].filter(id => !initialPeopleIds.has(id));
      const toRemove     = [...initialPeopleIds].filter(id => !selectedPeopleIds.has(id));
      const toAddEmp     = [...selectedEmployedIds].filter(id => !initialEmployedIds.has(id));
      const toRemoveEmp  = [...initialEmployedIds].filter(id => !selectedEmployedIds.has(id));
      const pendingBizConns = bizConnections.filter(c => c._pending);
      await Promise.all([
        ...toAdd.map(id       => peopleAPI.addLinkedBusiness(id, businessId)),
        ...toRemove.map(id    => peopleAPI.removeLinkedBusiness(id, businessId)),
        ...toAddEmp.map(id    => peopleAPI.addLinkedBusiness(id, businessId, 'employed')),
        ...toRemoveEmp.map(id => peopleAPI.removeLinkedBusiness(id, businessId, 'employed')),
        ...pendingBizConns.map(c =>
          businessAPI.addConnection(businessId, c.target_id, c.relationship_type, c.note)
        ),
      ]);

      onSave(savedBusiness);
    } catch (error) {
      console.error('Error saving business:', error);
      alert('Failed to save business: ' + error.message);
    }
  };


  const addBizConnection = async () => {
    if (!newBizConn.targetId) return;
    const targetBiz = allBusinesses.find(b => b.id === parseInt(newBizConn.targetId));
    if (!business) {
      // Queue locally for new businesses — saved after create
      setBizConnections(prev => [...prev, {
        _pending: true,
        target_id: parseInt(newBizConn.targetId),
        target_name: targetBiz?.name || `Business #${newBizConn.targetId}`,
        relationship_type: newBizConn.type,
        note: newBizConn.note,
      }]);
      setNewBizConn({ targetId: '', type: 'partner', note: '' });
      setShowBizConnForm(false);
      return;
    }
    try {
      await businessAPI.addConnection(business.id, parseInt(newBizConn.targetId), newBizConn.type, newBizConn.note);
      const updated = await businessAPI.getConnections(business.id);
      setBizConnections(updated.filter(c => c.source_business_id === business.id));
      setNewBizConn({ targetId: '', type: 'partner', note: '' });
      setShowBizConnForm(false);
    } catch (e) {
      alert('Failed to add connection: ' + e.message);
    }
  };

  const removeBizConnection = async (conn) => {
    if (conn._pending) {
      setBizConnections(prev => prev.filter(c => c !== conn));
      return;
    }
    try {
      await businessAPI.deleteConnection(business.id, conn.id);
      setBizConnections(prev => prev.filter(c => c.id !== conn.id));
    } catch (e) {
      alert('Failed to remove connection: ' + e.message);
    }
  };

  const geocodeAddress = async () => {
    if (!formData.address && !formData.city && !formData.country) {
      alert('Please enter an address, city, or country to geocode');
      return;
    }

    const query = [formData.address, formData.city, formData.state, formData.country]
      .filter(Boolean)
      .join(', ');

    try {
      const response = await fetch(
        `/api/geocode?q=${encodeURIComponent(query)}`,
        { credentials: 'include' }
      );
      const data = await response.json();

      if (response.ok && data.lat) {
        setFormData({ ...formData, latitude: data.lat, longitude: data.lng });
        alert('Location geocoded successfully!');
      } else {
        let msg = data.message || 'Could not find coordinates for this address.';
        if (data.reason === 'not_found') {
          msg += '\n\nTips:\n• Add city and country (e.g. "New York, USA")\n• Check for spelling mistakes';
        } else if (data.reason === 'timeout') {
          msg += '\n\nGeocoding timed out — check your connection and try again.';
        }
        alert(msg);
      }
    } catch (err) {
      console.error('Geocoding error:', err);
      alert('Geocoding service is unavailable. Check your connection.');
    }
  };

  const businessTypes = [
    'Corporation', 'LLC', 'Partnership', 'Sole Proprietorship', 
    'Non-Profit', 'Government', 'Other'
  ];

  const industries = [
    'Technology', 'Finance', 'Healthcare', 'Manufacturing', 'Retail',
    'Real Estate', 'Construction', 'Transportation', 'Education',
    'Entertainment', 'Hospitality', 'Other'
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b dark:border-gray-600">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
              <Building2 className="w-6 h-6 mr-2" />
              {business ? 'Edit Business' : 'Add New Business'}
            </h2>
            <button onClick={onCancel} className="text-gray-600 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-100">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Business Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Type</option>
                  {businessTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Industry</label>
                <select
                  value={formData.industry}
                  onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Industry</option>
                  {industries.map(industry => (
                    <option key={industry} value={industry}>{industry}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Owner</label>
                <select
                  value={formData.owner_person_id || ''}
                  onChange={(e) => setFormData({ ...formData, owner_person_id: e.target.value ? parseInt(e.target.value) : null })}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">No Owner Selected</option>
                  {people.map(person => (
                    <option key={person.id} value={person.id}>
                      {person.first_name} {person.last_name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="dissolved">Dissolved</option>
                </select>
              </div>
            </div>
          </div>

          {/* Location */}
          <div>
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <MapPin className="w-5 h-5 mr-2" />
              Location
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Address</label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Street address"
                  />
                  <button
                    type="button"
                    onClick={geocodeAddress}
                    className="px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                    title="Get coordinates"
                  >
                    <MapPin className="w-4 h-4" />
                  </button>
                </div>
                {formData.latitude && formData.longitude && (
                  <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                    Coordinates: {formData.latitude.toFixed(6)}, {formData.longitude.toFixed(6)}
                  </p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="City"
                  className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  placeholder="State/Province"
                  className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  placeholder="Country"
                  className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                  placeholder="Postal Code"
                  className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Contact Information</h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Phone"
                  className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Mail className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Email"
                  className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Globe className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="Website"
                  className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Registration Details */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Registration Details</h3>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                value={formData.registration_number}
                onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                placeholder="Registration Number"
                className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="date"
                value={formData.registration_date}
                onChange={(e) => setFormData({ ...formData, registration_date: e.target.value })}
                className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Employed At Selector */}
          <div>
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <Briefcase className="w-5 h-5 mr-2 text-emerald-500" />
              Employed At ({selectedEmployedIds.size})
            </h3>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={employedSearch}
                onChange={e => setEmployedSearch(e.target.value)}
                placeholder="Search people..."
                className="w-full pl-9 pr-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-slate-700 dark:border-gray-600 dark:text-gray-100"
              />
            </div>
            <div className="max-h-48 overflow-y-auto border rounded-md divide-y dark:border-gray-600 dark:divide-gray-600">
              {people
                .filter(p => {
                  const name = `${p.first_name} ${p.last_name || ''}`.toLowerCase();
                  return name.includes(employedSearch.toLowerCase());
                })
                .map(person => {
                  const isSelected = selectedEmployedIds.has(person.id);
                  return (
                    <label
                      key={person.id}
                      className={`flex items-center space-x-3 px-3 py-2 cursor-pointer transition-colors ${
                        isSelected
                          ? 'bg-emerald-50 dark:bg-emerald-900/20'
                          : 'hover:bg-gray-50 dark:hover:bg-slate-700'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {
                          const next = new Set(selectedEmployedIds);
                          isSelected ? next.delete(person.id) : next.add(person.id);
                          setSelectedEmployedIds(next);
                          if (!isSelected) {
                            const lnk = new Set(selectedPeopleIds);
                            lnk.delete(person.id);
                            setSelectedPeopleIds(lnk);
                          }
                        }}
                        className="h-4 w-4 text-emerald-600 rounded"
                      />
                      <User className="w-4 h-4 text-gray-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-sm text-gray-900 dark:text-slate-100">
                          {person.first_name} {person.last_name}
                        </span>
                        {person.category && (
                          <span className="ml-2 text-xs text-gray-500 dark:text-slate-400">
                            {person.category}
                          </span>
                        )}
                      </div>
                      {isSelected && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 shrink-0">
                          Employed
                        </span>
                      )}
                    </label>
                  );
                })}
              {people.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-slate-400 text-center py-4">No people found</p>
              )}
            </div>
          </div>

          {/* Linked People Selector */}
          <div>
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <Link className="w-5 h-5 mr-2" />
              Linked People ({selectedPeopleIds.size})
            </h3>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={peopleSearch}
                onChange={e => setPeopleSearch(e.target.value)}
                placeholder="Search people..."
                className="w-full pl-9 pr-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:border-gray-600 dark:text-gray-100"
              />
            </div>
            <div className="max-h-48 overflow-y-auto border rounded-md divide-y dark:border-gray-600 dark:divide-gray-600">
              {people
                .filter(p => {
                  const name = `${p.first_name} ${p.last_name || ''}`.toLowerCase();
                  return name.includes(peopleSearch.toLowerCase());
                })
                .map(person => {
                  const isSelected = selectedPeopleIds.has(person.id);
                  return (
                    <label
                      key={person.id}
                      className={`flex items-center space-x-3 px-3 py-2 cursor-pointer transition-colors ${
                        isSelected
                          ? 'bg-blue-50 dark:bg-blue-900/20'
                          : 'hover:bg-gray-50 dark:hover:bg-slate-700'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {
                          const next = new Set(selectedPeopleIds);
                          isSelected ? next.delete(person.id) : next.add(person.id);
                          setSelectedPeopleIds(next);
                          if (!isSelected) {
                            const emp = new Set(selectedEmployedIds);
                            emp.delete(person.id);
                            setSelectedEmployedIds(emp);
                          }
                        }}
                        className="h-4 w-4 text-blue-600 rounded"
                      />
                      <User className="w-4 h-4 text-gray-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-sm text-gray-900 dark:text-slate-100">
                          {person.first_name} {person.last_name}
                        </span>
                        {person.category && (
                          <span className="ml-2 text-xs text-gray-500 dark:text-slate-400">
                            {person.category}
                          </span>
                        )}
                      </div>
                      {isSelected && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 shrink-0">
                          Linked
                        </span>
                      )}
                    </label>
                  );
                })}
              {people.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-slate-400 text-center py-4">No people found</p>
              )}
            </div>
          </div>

          {/* Cases */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold flex items-center">
                <Folder className="w-5 h-5 mr-2 text-purple-500" />
                Cases {selectedCaseIds.length > 0 && <span className="ml-1 text-sm font-normal text-gray-500">({selectedCaseIds.length})</span>}
              </h3>
              <button type="button" onClick={() => { setShowCaseSearch(s => !s); setCaseSearch(''); }}
                className="px-3 py-1 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center text-sm">
                <Plus className="w-4 h-4 mr-1" />Add Case
              </button>
            </div>

            <div className="flex flex-wrap gap-1 mb-2">
              {selectedCaseIds.map(cid => {
                const c = allCases.find(x => x.id === cid);
                return c ? (
                  <span key={cid} className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">
                    {c.case_name}
                    <button type="button" onClick={() => setSelectedCaseIds(ids => ids.filter(id => id !== cid))}
                      className="ml-1 hover:text-red-500"><X className="w-3 h-3" /></button>
                  </span>
                ) : null;
              })}
              {selectedCaseIds.length === 0 && !showCaseSearch && (
                <span className="text-sm text-gray-400 dark:text-slate-500">No cases linked</span>
              )}
            </div>

            {showCaseSearch && (
              <div className="border dark:border-gray-600 rounded-lg overflow-hidden">
                <input type="text" value={caseSearch} onChange={e => setCaseSearch(e.target.value)}
                  autoFocus placeholder="Search cases…"
                  className="w-full px-3 py-2 text-sm border-b dark:border-gray-600 focus:outline-none dark:bg-slate-700 dark:text-gray-100" />
                <div className="max-h-36 overflow-y-auto">
                  {allCases
                    .filter(c => c.case_name.toLowerCase().includes(caseSearch.toLowerCase()))
                    .filter(c => !selectedCaseIds.includes(c.id))
                    .map(c => (
                      <button key={c.id} type="button"
                        onClick={() => { setSelectedCaseIds(ids => [...ids, c.id]); setShowCaseSearch(false); setCaseSearch(''); }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-purple-50 dark:hover:bg-purple-900/20 border-b dark:border-gray-700 last:border-0">
                        {c.case_name}
                      </button>
                    ))}
                  {allCases.filter(c => !selectedCaseIds.includes(c.id) && c.case_name.toLowerCase().includes(caseSearch.toLowerCase())).length === 0 && (
                    <p className="text-xs text-gray-400 text-center py-2">No more cases</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* B2B Connections */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold flex items-center">
                <Link className="w-5 h-5 mr-2 text-amber-500" />
                Business Connections ({bizConnections.length})
              </h3>
              <button type="button" onClick={() => setShowBizConnForm(!showBizConnForm)}
                className="px-3 py-1 bg-amber-500 text-white rounded-md hover:bg-amber-600 flex items-center text-sm">
                <Plus className="w-4 h-4 mr-1" />Add Connection
              </button>
            </div>

            {showBizConnForm && (() => {
              // Set of "businessId-type" pairs already connected
              const connectedPairs = new Set(
                bizConnections.map(c => `${c.target_id}-${c.relationship_type}`)
              );

              // Filter by selected type — same business can appear for a different type
              const availableBusinesses = allBusinesses
                .filter(b => b.id !== business?.id)
                .filter(b => !connectedPairs.has(`${b.id}-${newBizConn.type}`));

              const handleTypeChange = (newType) => {
                const stillValid = newBizConn.targetId &&
                  !connectedPairs.has(`${newBizConn.targetId}-${newType}`);
                setNewBizConn({
                  ...newBizConn,
                  type: newType,
                  targetId: stillValid ? newBizConn.targetId : '',
                });
              };

              return (
                <div className="mb-3 p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    {/* Type first — determines which businesses appear */}
                    <select value={newBizConn.type}
                      onChange={e => handleTypeChange(e.target.value)}
                      className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 dark:bg-slate-700 dark:border-gray-600 dark:text-gray-100">
                      {B2B_RELATIONSHIP_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>

                    {/* Business dropdown — filtered by selected type */}
                    <select value={newBizConn.targetId}
                      onChange={e => setNewBizConn({ ...newBizConn, targetId: e.target.value })}
                      className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 dark:bg-slate-700 dark:border-gray-600 dark:text-gray-100">
                      <option value="">
                        {availableBusinesses.length === 0
                          ? `All businesses already linked as "${B2B_RELATIONSHIP_TYPES.find(t => t.value === newBizConn.type)?.label}"`
                          : 'Select business…'}
                      </option>
                      {availableBusinesses.map(b => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>

                    <input type="text" placeholder="Note (optional)"
                      value={newBizConn.note}
                      onChange={e => setNewBizConn({ ...newBizConn, note: e.target.value })}
                      className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 dark:bg-slate-700 dark:border-gray-600 dark:text-gray-100" />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button type="button" onClick={() => setShowBizConnForm(false)}
                      className="px-3 py-1 border rounded-lg text-sm text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700">
                      Cancel
                    </button>
                    <button type="button" onClick={addBizConnection}
                      disabled={!newBizConn.targetId}
                      className="px-3 py-1 bg-amber-500 text-white rounded-lg text-sm hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed">
                      Add
                    </button>
                  </div>
                </div>
              );
            })()}

            {bizConnections.length > 0 && (
              <div className="space-y-2">
                {bizConnections.map((conn, i) => (
                  <div key={conn.id || i}
                    className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Building2 className="w-4 h-4 text-amber-500 shrink-0" />
                      <div>
                        <span className="font-medium text-sm text-gray-900 dark:text-slate-100">
                          {conn.target_name || `Business #${conn.target_id}`}
                        </span>
                        <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                          {B2B_RELATIONSHIP_TYPES.find(t => t.value === conn.relationship_type)?.label || conn.relationship_type}
                        </span>
                        {conn.note && <span className="ml-2 text-xs text-gray-500 italic">{conn.note}</span>}
                      </div>
                    </div>
                    <button type="button" onClick={() => removeBizConnection(conn)}
                      className="text-red-500 hover:text-red-700 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="3"
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-700 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {business ? 'Update' : 'Create'} Business
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditBusinessForm;